"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { CategorySlug } from "@/lib/category/types";

interface SwitcherCategory {
	slug: CategorySlug | "all";
	label: string;
	count?: number;
	icon?: ReactNode;
}

interface CategorySwitcherProps {
	categories: SwitcherCategory[];
	/** `"all"` when on the product catalog, otherwise the detail slug. */
	active: CategorySlug | "all";
	locale: string;
	channel: string;
	navigationLabel?: string;
}

export function CategorySwitcher({
	categories,
	active,
	locale,
	channel,
	navigationLabel,
}: CategorySwitcherProps) {
	const activeRef = useRef<HTMLAnchorElement | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const container = scrollRef.current;
		const activeLink = activeRef.current;
		if (!container || !activeLink || container.scrollWidth <= container.clientWidth) return;
		// Move this row only. scrollIntoView can also move the page past the hero.
		const containerBounds = container.getBoundingClientRect();
		const activeBounds = activeLink.getBoundingClientRect();
		container.scrollLeft +=
			activeBounds.left - containerBounds.left - (container.clientWidth - activeBounds.width) / 2;
	}, [active]);

	return (
		<nav aria-label={navigationLabel} className="border-b border-border bg-secondary">
			<div className="mx-auto max-w-7xl px-4 pb-3 sm:px-6">
				<div
					ref={scrollRef}
					className="flex gap-2 overflow-x-auto rounded-lg border border-border p-1 [scrollbar-width:thin] md:flex-wrap md:border-0 md:p-0"
				>
					{categories.map((cat) => {
						const isActive = cat.slug === active;
						const href =
							cat.slug === "all"
								? `/${locale}/${channel}/products`
								: `/${locale}/${channel}/categories/${cat.slug}`;
						return (
							<Link
								key={cat.slug}
								href={href}
								ref={isActive ? activeRef : undefined}
								aria-current={isActive ? "page" : undefined}
								className={
									"inline-flex min-h-11 max-w-[calc(100vw-4rem)] shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring " +
									(isActive
										? "border-foreground bg-foreground text-background"
										: "border-border bg-background text-foreground hover:bg-secondary")
								}
							>
								{cat.icon}
								<span className="min-w-0 hyphens-auto break-words">{cat.label}</span>
								{typeof cat.count === "number" && <span className="shrink-0 text-xs">{cat.count}</span>}
							</Link>
						);
					})}
				</div>
			</div>
		</nav>
	);
}
